// Global server-fn middleware that enforces account suspension at the API
// boundary. Any server function called with a Supabase Bearer token belonging
// to a suspended account is rejected with HTTP 403 before reaching its
// handler — even if the handler itself does not opt into requireSupabaseAuth.
//
// This is intentionally redundant with the per-route client gate in
// src/routes/_authenticated/route.tsx so suspended users cannot bypass
// enforcement by hitting server functions directly (e.g. from a stale tab or
// via a crafted request).

import { createMiddleware } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "./types";

// Server functions that must remain callable even when an account is
// suspended. Analytics/logging endpoints must never throw — a 403 here
// surfaces as an unhandled error in the client overlay during the
// sign-out-and-redirect race.
const SUSPENSION_EXEMPT_EXPORTS = new Set<string>([
  "logCtaEvent_createServerFn_handler",
  "getOnboardingDestination_createServerFn_handler",
  "getCtaContext_createServerFn_handler",
  "resolveCtaToken_createServerFn_handler",
  "getCtaShiftPreview_createServerFn_handler",
  "consumeCtaToken_createServerFn_handler",
]);

function getServerFnExport(request: Request | undefined): string | null {
  try {
    if (!request) return null;
    const url = new URL(request.url);
    const seg = url.pathname.split("/").filter(Boolean).pop();
    if (!seg) return null;
    const b64 = seg.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = typeof atob === "function"
      ? atob(padded)
      : Buffer.from(padded, "base64").toString("utf8");
    const parsed = JSON.parse(json) as { export?: string };
    return parsed.export ?? null;
  } catch {
    return null;
  }
}

export const enforceNotSuspended = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    const request = getRequest();
    const authHeader = request?.headers.get("authorization");

    if (!authHeader || !authHeader.startsWith("Bearer ")) {
      return next();
    }
    const token = authHeader.slice("Bearer ".length).trim();
    if (!token) return next();

    const exportName = getServerFnExport(request);
    if (exportName && SUSPENSION_EXEMPT_EXPORTS.has(exportName)) {
      return next();
    }

    const SUPABASE_URL = process.env.SUPABASE_URL;
    const SUPABASE_PUBLISHABLE_KEY = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) return next();

    const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      global: { headers: { Authorization: `Bearer ${token}` } },
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    });

    const { data: claimsRes, error: claimsErr } = await supabase.auth.getClaims(token);
    const uid = claimsRes?.claims?.sub;
    if (claimsErr || !uid) return next();

    const { data: profile } = await supabase
      .from("profiles")
      .select("is_suspended, suspension_reason")
      .eq("id", uid)
      .maybeSingle();

    if (profile?.is_suspended) {
      const reason = (profile.suspension_reason ?? "Your account has been suspended.").slice(0, 240);
      throw new Response(
        JSON.stringify({ error: "account_suspended", reason }),
        {
          status: 403,
          headers: { "content-type": "application/json; charset=utf-8" },
        },
      );
    }

    return next();
  },
);
